import { AgeLevel } from 'types/api/envScales/AgeLevel';
import { Domain } from 'types/api/envScales/Domain';
import { Assessment } from 'types/api/envScales/Assessment';
import { MultiSelectOptionGroup } from '@teachstonellc/component-library';

/**
 * An empty `dimension_ids` means "no narrowing", not "nothing selected":
 * formal observations never set one, and informal observations created before
 * the feature have none. Both keep showing every dimension.
 */
export function isFocusedAssessment(assessment: Assessment) {
  return Boolean(assessment.informal && assessment.dimension_ids?.length);
}

/** Drops non-focus dimensions, then any domain left with none. */
export function filterDomains(
  domains: Domain[],
  dimensionIds: number[] | null | undefined
): Domain[] {
  if (!dimensionIds?.length) {
    return domains;
  }

  return domains
    .map(domain => ({
      ...domain,
      dimensions: domain.dimensions.filter(dimension =>
        dimensionIds.includes(dimension.id)
      )
    }))
    .filter(domain => domain.dimensions.length > 0);
}

/**
 * The age level as the observation's scoring surfaces should see it.
 *
 * Deliberately not applied in `context/measure`: the summary renders every
 * dimension off the same context and must keep seeing the full catalog.
 */
export function getFocusedAgeLevel(
  ageLevel: AgeLevel,
  assessment: Assessment
): AgeLevel {
  if (!isFocusedAssessment(assessment)) {
    return ageLevel;
  }

  return {
    ...ageLevel,
    domains: filterDomains(ageLevel.domains, assessment.dimension_ids)
  };
}

/** Every dimension id of an age level, in catalog order. */
export function getAgeLevelDimensionIds(ageLevel: AgeLevel): number[] {
  return ageLevel.domains.flatMap(domain =>
    domain.dimensions.map(dimension => dimension.id)
  );
}

/** One MultiSelect group per domain, in catalog order. */
export function buildFocusOptions(
  ageLevel: AgeLevel
): MultiSelectOptionGroup[] {
  return ageLevel.domains.map(domain => ({
    title: domain.name,
    options: domain.dimensions.map(dimension => ({
      value: dimension.id,
      text: dimension.name
    }))
  }));
}
